/**
 * Canal de venda REAL do pedido (o que o Bling informa: Mercado Livre, Shopee, Magalu, ...).
 *
 * `origin` (loja/whatsapp/marketplace/manual) é o vocabulário do fluxo interno e continua
 * existindo; o canal de venda é a conta de marketplace/loja que originou o pedido. Pedidos
 * sem `channelKind` (Loja, WhatsApp, manuais) caem no canal da própria origem — nenhum pedido
 * fica sem canal e nenhum canal é inventado. Puro e sem I/O.
 */
import { isRevenueState } from "./state-machine.js";
import type { PlatformUnifiedOrder } from "../api/types.js";

export const SALES_CHANNEL_LABELS: Record<string, string> = {
  mercado_livre: "Mercado Livre",
  shopee: "Shopee",
  magalu: "Magalu",
  amazon: "Amazon",
  shein: "Shein",
  tiktok: "TikTok Shop",
  integracommerce: "Integracommerce",
  bling_manual: "Venda direta (Bling)",
  loja: "Loja Super2 (site)",
  whatsapp: "WhatsApp",
  manual: "Venda manual",
  marketplace: "Marketplace",
  outro: "Outros canais"
};

export function salesChannelKey(order: Pick<PlatformUnifiedOrder, "origin" | "channelKind">): string {
  return order.channelKind || order.origin;
}

export function salesChannelLabel(key: string): string {
  return SALES_CHANNEL_LABELS[key] ?? key;
}

export interface SalesChannelBucket {
  key: string;
  label: string;
  count: number;
  /** Pedidos que contam como receita (estados de receita). */
  revenueOrders: number;
  revenueCents: number;
  /** Pedidos em aberto no Bling (situação "Em aberto"), para a fila de faturamento. */
  openInBling: number;
}

/** Quantos pedidos e quanta receita por canal de venda, do maior para o menor; canais sem pedido não aparecem. */
export function salesChannelBuckets(orders: PlatformUnifiedOrder[]): SalesChannelBucket[] {
  const byKey = new Map<string, SalesChannelBucket>();
  for (const order of orders) {
    const key = salesChannelKey(order);
    const bucket = byKey.get(key) ?? { key, label: salesChannelLabel(key), count: 0, revenueOrders: 0, revenueCents: 0, openInBling: 0 };
    bucket.count += 1;
    if (isRevenueState(order.state)) {
      bucket.revenueOrders += 1;
      bucket.revenueCents += order.totalCents ?? 0;
    }
    if ((order.blingSituation ?? "").toLowerCase().includes("aberto")) bucket.openInBling += 1;
    byKey.set(key, bucket);
  }
  return [...byKey.values()].sort((a, b) => b.revenueCents - a.revenueCents || b.count - a.count);
}

export function ordersOfChannel(orders: PlatformUnifiedOrder[], key: string): PlatformUnifiedOrder[] {
  return orders.filter((order) => salesChannelKey(order) === key);
}
